import { Rss } from 'lucide-react'
import { type LinkItem } from '../../ErrorPage/Links'

export type StatusCode =
  | 404
  | 500
  | 'user404'
  | 'post404'
  | 'page404'
  | 'group404'
  | 'groupHidden'
  | 'RBACInsufficientPermissions'

export type ErrorBody = {
  title?: string
  description?: string
  links?: LinkItem[]
}

type ErrorData = {
  user: ErrorBody
  guest?: ErrorBody
}

export const COMMON_404_ERROR = {
  title: 'Oops! We can’t find that page',
  description:
    'This page isn’t available. It may have been removed or you don’t have permission to view it',
}

const errorsData: {
  [key in StatusCode]: ErrorData
} = {
  404: {
    user: {
      ...COMMON_404_ERROR,
      links: [
        {
          text: 'Go to feed',
          link: '/feed',
          icon: <Rss size={20} color="white" />,
        },
        { text: 'Discover groups', link: '/discover' },
      ],
    },
    guest: {
      ...COMMON_404_ERROR,
      links: [
        { text: 'Get started', link: '/signin' },
        { text: 'Go to homepage', link: '/' },
      ],
    },
  },
  500: {
    user: {
      title: 'Oops, something went wrong!',
      description:
        'We are already on it. Please try again later or contact us at <a href="mailto:support@wave.video">support@wave.video</a>',
    },
  },
  user404: {
    user: {
      title: 'We could not find the user you were looking for',
      description: 'Check the username and try one more time',
      links: [
        { text: 'Wellsley account', link: '/account' },
        { text: 'account settings', link: '/account/settings' },
      ],
    },
  },
  group404: {
    user: {
      ...COMMON_404_ERROR,
      links: [{ text: 'Go to group', link: '/discover' }],
    },
  },
  groupHidden: {
    user: {
      title: 'This is a hidden private group',
      description:
        'Access is restricted — you can join only via a direct invitation link from the owner',
    },
  },
  page404: {
    user: {
      title: 'We could not find the page you were looking for',
      description: 'Check the address and try one more time',
    },
  },
  post404: {
    user: {
      title: 'We could not find the post you were looking for',
      description: 'Check the address and try one more time',
    },
  },
  RBACInsufficientPermissions: {
    user: {
      title: "You don't have permission to perform this action",
      description: 'Please contact the administrator to get access',
    },
  },
}

export const isErrorBody = (error: unknown): error is ErrorBody => {
  const { title, description, links } = (error as ErrorBody) || {}
  return !!(title || description || links)
}

export const isStatusError = (code: unknown): code is StatusCode =>
  Object.keys(errorsData).some((k) => k == code)

const DEFAULT_ERROR_CODE = 404

export const getErrorByStatusCode = (
  statusCode: StatusCode = DEFAULT_ERROR_CODE,
  isLogged = true,
) => {
  const errors = errorsData[statusCode]
  const userError = errors?.user

  return isLogged ? userError : errors.guest || userError
}
